import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatINR } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, Download, Trash2, Pencil } from 'lucide-react';

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700',
  Sent: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Order: 'bg-amber-100 text-amber-700',
  Delivered: 'bg-purple-100 text-purple-700',
  Installed: 'bg-emerald-100 text-emerald-700',
};
const STATUSES = ['Draft', 'Sent', 'Approved', 'Order', 'Delivered', 'Installed'];

export default function QuotationsPage() {
  const [quotations, setQuotations] = useState([]);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const { data } = await api.get('/quotations', { params });
      setQuotations(data);
    } catch {}
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/quotations/${id}/status`, { status });
      load();
    } catch {}
  };

  const downloadPdf = async (id, quoteNumber) => {
    try {
      const { data } = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quoteNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const deleteQuotation = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    await api.delete(`/quotations/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Quotations</h1>
          <p className="text-slate-500 text-sm mt-1">{quotations.length} quotation{quotations.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="bg-[#ed8936] hover:bg-[#dd6b20] text-white" onClick={() => navigate('/quotations/new')} data-testid="create-quotation-btn">
            <Plus size={16} className="mr-1" /> New Quotation
          </Button>
        </div>
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {quotations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quotations-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Quote #</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Customer</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Items</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Grand Total</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quotations.map(q => (
                    <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/quotations/${q.id}`)} className="font-mono text-xs font-medium text-[#1a365d] hover:text-[#ed8936] transition-colors" data-testid={`quote-link-${q.id}`}>
                          {q.quote_number}
                        </button>
                      </td>
                      <td className="px-4 py-3">{q.customer_name || '-'}</td>
                      <td className="px-4 py-3 text-slate-500">{q.date?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">{q.items?.length || 0}</td>
                      <td className="px-4 py-3 text-right font-medium font-mono">{formatINR(q.grand_total)}</td>
                      <td className="px-4 py-3">
                        <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                          <SelectTrigger className="h-7 w-28 text-xs border-none p-0">
                            <Badge variant="secondary" className={STATUS_COLORS[q.status] || ''}>{q.status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/quotations/${q.id}/edit`)} className="text-slate-500 hover:text-[#1a365d]" data-testid={`edit-quotation-${q.id}`} title="Edit quotation">
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => downloadPdf(q.id, q.quote_number)} className="text-[#1a365d] hover:text-[#ed8936]" data-testid={`download-pdf-${q.id}`} title="Download PDF">
                            <Download size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteQuotation(q.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-quotation-${q.id}`} title="Delete">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <FileText size={48} className="mb-3 opacity-30" />
              <p className="text-sm">No quotations found. Create your first quotation.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
